import { useEffect, useRef, useState } from 'react';
import { nf, type Favorito } from './tipos';

/** Porcentaje del estudio para la barra del listado; null si aún no hay total. */
export function progresoEstudio(f: Favorito): { pct: number | null; etiqueta: string; activo: boolean } {
  const total = f.docs_total ?? 0, hechos = f.docs_listos ?? 0;
  const activo = f.sync_estado === 'ingestando' || f.sync_estado === 'enriqueciendo' || f.sync_estado === 'analizando';
  if (f.sync_estado === 'listo') {
    // "listo" con docs sin texto (imágenes/audios → Fase 2, archivos enormes): se muestra la fracción real.
    const pct = total > 0 ? Math.round((hechos / total) * 100) : 100;
    return { pct: 100, etiqueta: pct < 100 ? `listo · ${pct} %` : 'listo', activo: false };
  }
  if (total > 0) { const pct = Math.min(99, Math.round((hechos / total) * 100)); return { pct, etiqueta: `${pct} %`, activo }; }
  if (f.sync_estado === 'analizado') return { pct: 0, etiqueta: 'por estudiar', activo: false };
  if (f.sync_estado === 'error') return { pct: null, etiqueta: 'interrumpido', activo: false };
  return { pct: null, etiqueta: 'sin preparar', activo };
}

/** Línea de estado del estudio de una DIA: punto de color + texto, sin chips que quiebren. */
export function EstadoEstudio({ f }: { f: Favorito }) {
  const activo = f.sync_estado === 'ingestando' || f.sync_estado === 'enriqueciendo';
  // Tick de 1s: el contador de tiempo restante se recalcula continuo. El ritmo se mide en
  // VENTANA DESLIZANTE (últimos ~90s), no promedio histórico: una ráfaga de saltos (docs
  // ya estudiados al reanudar) inflaría el ritmo y prometería "1 min" antes de tiempo;
  // con ventana, el contador se corrige solo apenas cambia la velocidad real.
  const [, setTick] = useState(0);
  const muestrasRef = useRef<{ t: number; hechos: number }[]>([]);
  useEffect(() => {
    if (!activo) { muestrasRef.current = []; return; }
    const t = window.setInterval(() => setTick((v) => v + 1), 1000);
    return () => window.clearInterval(t);
  }, [activo]);

  let eta = '';
  if (activo) {
    const hechos = f.docs_listos ?? 0;
    const total = f.docs_total ?? 0;
    const ahora = Date.now();
    const m = muestrasRef.current;
    if (m.length === 0 || m[m.length - 1]!.hechos !== hechos) m.push({ t: ahora, hechos });
    while (m.length > 2 && ahora - m[0]!.t > 90_000) m.shift();
    const base = m[0]!;
    const dt = (ahora - base.t) / 1000;
    const dHechos = hechos - base.hechos;
    if (dt >= 8 && dHechos >= 5 && total > hechos) {
      const seg = (total - hechos) / (dHechos / dt);
      eta = seg < 75 ? ' · queda menos de 1 min' : ` · ~${nf.format(Math.ceil(seg / 60))} min restantes`;
    } else if (f.sync_estado === 'ingestando' && f.analisis?.estFase1Min) {
      eta = ` · ~${f.analisis.estFase1Min} min (estimado)`;
    } else if (f.sync_estado === 'enriqueciendo' && f.analisis?.estFase2Min) {
      eta = ` · ~${f.analisis.estFase2Min} min (estimado)`;
    }
  }
  const progreso = (f.docs_total ?? 0) > 0 ? ` (${nf.format(f.docs_listos ?? 0)}/${nf.format(f.docs_total ?? 0)}${eta})` : '';
  const [punto, texto, titulo] =
    f.sync_estado === 'ingestando'
      ? ['bg-azul-profundo animate-pulse', `Estudiando…${progreso}`, 'Fase 1: Andrés extrae el texto de los documentos (si se interrumpe, retoma solo)']
      : f.sync_estado === 'analizando'
        ? ['bg-celeste animate-pulse', 'Analizando el directorio…', 'Se recorre la carpeta para estimar el estudio (sin descargar nada)']
        : f.sync_estado === 'analizado'
          ? ['bg-amber-400', 'Análisis listo — revisa y lanza el estudio', 'Abre "Ver análisis" para ver el resumen y lanzar la fase 1']
          : f.sync_estado === 'enriqueciendo'
            ? ['bg-azul-medio animate-pulse', `Transcribiendo multimedia…${progreso}`, 'Fase 2: Iris (imágenes) y Eco (audios) transcriben en segundo plano']
            : f.sync_estado === 'error'
              ? ['bg-red-500', 'Estudio interrumpido', 'La última sincronización se interrumpió — usa Sincronizar para reintentar']
              : f.sync_estado === 'listo'
                ? ['bg-emerald-500', `${nf.format(f.docs_listos ?? 0)} de ${nf.format(f.docs_total ?? 0)} docs estudiados`, f.sync_at ? `al día al ${new Date(f.sync_at).toLocaleString('es-CL')}` : '']
                : ['bg-amber-400', 'Sin preparar', 'Usa Preparar para que Andrés estudie la carpeta'];
  return (
    <p className="flex items-center gap-1.5 text-[12px] font-normal text-gris" title={titulo}>
      <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${punto}`} />
      {texto}
    </p>
  );
}

